import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';
import { fetchCatalogRequestCount } from '../api/client';
import { isLoggedIn } from '../lib/auth';
import { MENU_GROUPS, findMenuPlace, type EMenuAlarm, type MenuGroup, type MenuItem } from './menu';

interface AppSidebarData {
  /** 현재 활성 path 의 prefix (예: '/inventory' 면 /inventory/* 모두 활성) */
  activePath: string;
  /** 알람마다 센 수. 아직 안 셌거나 실패면 키가 없다 → 숫자 안 보임 */
  alarms: Partial<Record<EMenuAlarm, number>>;
}

/** 알람 하나를 세는 함수 — 종류마다 하나. 새 알람은 여기 한 줄 + menu.ts 의 E_MENU_ALARMS */
const ALARM_COUNTERS: Record<EMenuAlarm, () => Promise<number>> = {
  catalog_drafted: () => fetchCatalogRequestCount('drafted'),
};

interface NavItem {
  icon: string;
  label: string;
  href: string;
  /** 이 prefix 로 시작하는 모든 path 에서 활성으로 표시 */
  matchPrefix: string;
}

const NAV_ITEMS: NavItem[] = [
  { icon: '🤝', label: '딜',        href: '/deals',      matchPrefix: '/deals' },
  { icon: '🧭', label: '소싱',      href: '/sourcing',   matchPrefix: '/sourcing' },
  { icon: '🏠', label: '대시보드',  href: '/dashboard',  matchPrefix: '/dashboard' },
  // lead / rfq 는 deal/quote 로 흡수 — 메뉴 숨김 (라우트는 deprecated 로 유지)
  // { icon: '📥', label: '리드',      href: '/leads',      matchPrefix: '/leads' },
  // { icon: '📝', label: '견적(RFQ)', href: '/rfq',        matchPrefix: '/rfq' },
  // 카탈로그(/catalog) · 임시등록(/catalog-pending)은 NEXT 의 카탈로그 › 검색 · 요청이 대신한다 — 같은 주소가 둘 다 활성으로 보였다(QA 2026-10-20 #27). 라우트는 남는다
  { icon: '🏢', label: '거래처',    href: '/suppliers',  matchPrefix: '/suppliers' },
  { icon: '🐞', label: '이슈',      href: '/issues',     matchPrefix: '/issues' },
  { icon: '⚙️', label: '설정',      href: '/settings',   matchPrefix: '/settings' },
];

/**
 * 🎯 AppSidebar — 어드민 앱 메인 사이드바 (6섹션)
 *
 * 라우터 인스턴스를 모름. `<a data-link href="...">` 사용 → 라우터가
 * document 레벨에서 클릭 인터셉트.
 *
 * active 표시는 AppShell 이 route:ready 이벤트마다 setActivePath() 호출.
 */
export class AppSidebar extends SolaView<AppSidebarData> {
  constructor() {
    super();
    this.data = { activePath: '/', alarms: {} };
  }

  /** 경로가 바뀔 때마다 — active 토글만. 알람은 refreshAlarms 로 따로(첫 화면에서 attach 의 route:ready 와 최초 표시가 둘 다 불러 두 번 세던 것 — QA 2026-10-20 #14) */
  setActivePath(path: string): void {
    this.data.activePath = path;
    this.rerenderActive();
  }

  /** 알람 다시 세기 — 화면을 옮기면 수가 바뀌었을 수 있다. route:ready 마다 한 번. 실패는 조용히 — 숫자만 안 보인다 */
  refreshAlarms(): void {
    void this.countAlarms();
  }

  render(): HTMLElement {
    return htmlSolaWithInstance(this)`
      <aside class="app-sidebar">
        <header class="app-sidebar-header">
          <div class="app-logo">
            <span class="app-logo-mark">⬢</span>
            <span class="app-logo-text">
              HCLTech<br />
              <small>어드민</small>
            </span>
          </div>
        </header>
        <nav class="app-sidebar-nav">
          ${this.renderNavItems()}
        </nav>
        <footer class="app-sidebar-footer">
          <span class="app-build-info">v0.1 · Phase A</span>
        </footer>
      </aside>
    `;
  }

  /** 초기 렌더용 — innerHTML 직접 빌드. v1 메뉴 · 선 · 메뉴 묶음(제품 · 재고, 현민 2026-10-07) */
  private renderNavItems(): string {
    return [
      ...NAV_ITEMS.map((item) => this.renderNavItem(item)),
      `<div class="app-nav-divider"><span>next</span></div>`,
      ...MENU_GROUPS.map((group) => this.renderMenuGroup(group)),
    ].join('');
  }

  /** 묶음 제목 한 줄 + 메뉴 한 줄에 하나(탭은 페이지 안). 메뉴는 제 탭 중 하나가 열려 있을 때 활성 */
  private renderMenuGroup(group: MenuGroup): string {
    const items = group.items.map((item) => this.renderMenuItem(item)).join('');
    return `<div class="app-nav-group-title"><span class="app-nav-icon">${group.icon}</span><span>${group.title}</span></div>${items}`;
  }

  private renderMenuItem(item: MenuItem): string {
    const active = this.isMenuActive(item) ? ' active' : '';
    const count = this.countOf(item);
    const badge = count === null ? '' : ` <span class="app-nav-count">${count}</span>`;
    const title = item.tabs.map((tab) => tab.label).join(' · ');
    return `
      <a class="app-nav-item app-nav-sub${active}" data-link href="${item.href}" data-nav="${item.href}" data-nav-menu="${item.label}" title="탭: ${title}">
        <span class="app-nav-label">${item.label}${badge}</span>
      </a>
    `;
  }

  private renderNavItem(item: NavItem): string {
    const active = this.isActive(item) ? ' active' : '';
    return `
      <a class="app-nav-item${active}" data-link href="${item.href}" data-nav="${item.matchPrefix}">
        <span class="app-nav-icon">${item.icon}</span>
        <span class="app-nav-label">${item.label}</span>
      </a>
    `;
  }

  private isMenuActive(item: MenuItem): boolean {
    const place = findMenuPlace(this.data.activePath);
    return place !== null && place.item === item;
  }

  /** 메뉴의 숫자 — 알람이 있으면 센 수(0 이면 안 보임), 없으면 고정값 */
  private countOf(item: MenuItem): number | null {
    if (item.alarm === null) return item.count;
    const counted = this.data.alarms[item.alarm];
    return counted === undefined || counted === 0 ? null : counted;
  }

  /** 알람 전부를 세고 숫자만 다시 그린다. 한 알람이 실패해도 나머지는 센다. 로그인 전엔 안 센다(401 → 로그인 페이지로 튕기는 고리를 안 만든다) */
  private async countAlarms(): Promise<void> {
    if (!isLoggedIn()) return;
    const entries = await Promise.all(
      (Object.keys(ALARM_COUNTERS) as EMenuAlarm[]).map(async (alarm): Promise<[EMenuAlarm, number | null]> => {
        try { return [alarm, await ALARM_COUNTERS[alarm]()]; } catch { return [alarm, null]; }
      }),
    );
    const alarms: Partial<Record<EMenuAlarm, number>> = {};
    for (const [alarm, count] of entries) {
      if (count !== null) alarms[alarm] = count;
    }
    this.data.alarms = alarms;
    this.rerenderCounts();
  }

  /** 알람 숫자만 — 메뉴 줄의 라벨을 다시 쓴다(재마운트 안 함) */
  private rerenderCounts(): void {
    const labels = this._rootElement?.querySelectorAll<HTMLElement>('.app-nav-sub .app-nav-label');
    if (!labels) return;
    labels.forEach((el) => {
      const menuLabel = el.closest<HTMLElement>('.app-nav-item')?.dataset.navMenu;
      const item = MENU_GROUPS.flatMap((group) => group.items).find((candidate) => candidate.label === menuLabel);
      if (item === undefined) return;
      const count = this.countOf(item);
      el.innerHTML = `${item.label}${count === null ? '' : ` <span class="app-nav-count">${count}</span>`}`;
    });
  }

  /** 활성 path 변경 시 — DOM 의 active 클래스만 토글 (재마운트 안 함) */
  private rerenderActive(): void {
    const items = this._rootElement?.querySelectorAll<HTMLElement>('.app-nav-item');
    if (!items) return;
    items.forEach((el) => {
      const prefix = el.dataset.nav || '';
      const menu = el.dataset.navMenu;
      const isActive = menu === undefined ? this.matchActive(prefix) : this.isMenuLabelActive(menu);
      el.classList.toggle('active', isActive);
    });
  }

  /**
   * '/' 는 정확히 일치할 때만, 나머지는 prefix 매칭.
   * 예: activePath='/inventory/42' → '/inventory' 활성, '/' 비활성
   */
  private matchActive(prefix: string): boolean {
    const path = this.data.activePath;
    if (prefix === '/') return path === '/';
    // 홈('/')은 딜 리스트 — '딜' 메뉴를 활성으로.
    if (prefix === '/deals' && path === '/') return true;
    return path === prefix || path.startsWith(prefix + '/');
  }

  private isMenuLabelActive(label: string): boolean {
    const place = findMenuPlace(this.data.activePath);
    return place !== null && place.item.label === label;
  }

  private isActive(item: NavItem): boolean {
    return this.matchActive(item.matchPrefix);
  }
}
